import React, { Component } from "react";
import { Button } from "react-bootstrap";
import { FaCompass, FaWifi } from "react-icons/fa6";

import GameService from "../services/game";
import { readRemoteSession, clearRemoteSession } from "../context/RemoteLaunchContext";
import { restartPath } from "../services/visit";
import {
    readPosition, permissionState,
    GRANTED} from "../services/geo";

import "./OnsiteGate.css";

/**
 * The on-site gate: ask for location, send the reading, report the verdict.
 *
 * Rendered by every game page when the server says this promo is gated and
 * this session has not cleared it. Two shapes, because the games differ in
 * what is worth reading when you cannot play:
 *
 *   inline  — a banner. Survivor uses this, because its pre-pick screen shows
 *             a player's whole season and their standings, and all of that is
 *             worth looking at from home. The play affordances gray out and
 *             everything else stays live. This *is* the read-only status view.
 *   overlay — a panel over the screen. The other four have no equivalent
 *             read-only state; a matchup grid you cannot pick from is not
 *             something to browse, it is a tease.
 *
 * All copy comes from the server descriptor. The two refusals are the client's
 * promotion rules, and an operator who wants them reworded should not need a
 * frontend deploy — same reasoning as Survivor's server-authored selector copy.
 */
class OnsiteGate extends Component {
    constructor(props){
        super(props);
        this.state = {
            dismissed: false,
            // "position" | "server" | false. Kept apart because they are
            // different lies to tell: one blames the player's browser, the
            // other is ours.
            failure: false,
            checking: false,
            // null until we have actually tried. Distinct from DENIED: before
            // the first attempt we have no grounds to tell anybody anything.
            outcome: null,
            error: false,
        };
        this.mounted = false;
    }

    componentDidMount(){
        this.mounted = true;
        // GEO-612 -- they closed the tab or walked away. Bound once and
        // removed on unmount; pagehide rather than unload because iOS Safari
        // does not reliably fire unload, and unload disqualifies the page from
        // the back/forward cache.
        this.onLeave = this.leave.bind(this);
        window.addEventListener("pagehide", this.onLeave);
        this.silentCheck();
    }

    /* The pages that mount this fetch their promo after their first render, so
     * promoId can be undefined for a tick. Waiting for it is not fussiness: a
     * verify with no promo named cannot be answered promo-specifically, and the
     * server would fall back to the venue's default — which is the wrong answer
     * for exactly the promo that overrode it. */
    componentDidUpdate(prevProps){
        if(!prevProps.promoId && this.props.promoId) this.silentCheck();
    }

    componentWillUnmount(){
        this.mounted = false;
        if(this.onLeave) window.removeEventListener("pagehide", this.onLeave);
    }

    /**
     * GEO-610 -- record an outcome, at most once each, and never care whether
     * it landed.
     *
     * The whole point of this reporting is to turn "no row at all" into
     * something readable, so it must not itself be able to cost a player
     * anything: no state is set, no promise is awaited, no failure is
     * surfaced. If the post is dropped we lose a log line and the player
     * loses nothing.
     */
    report(outcome){
        if(!outcome) return;
        this.reported = this.reported || {};
        if(this.reported[outcome]) return;
        this.reported[outcome] = true;
        GameService.reportOnsite(this.sessionId(), this.props.promoId, outcome);
    }

    /**
     * GEO-612 -- left without answering.
     *
     * Only interesting while the outcome is still null. Once they have
     * declined, or the device has failed to get a fix, that row already says
     * what happened and their leaving afterwards adds nothing.
     */
    leave(){
        if(this.state.outcome !== null) return;
        this.report("left");
    }

    /**
     * Re-verify without prompting, when and only when that is possible.
     *
     * If permission is already granted the read raises no prompt, so a
     * returning player standing in the building sees the gate resolve itself
     * and never learns it was there. Every other state waits for a tap — see
     * services/geo.js for why an unsolicited prompt is worse than no prompt.
     */
    silentCheck(){
        if(!this.props.promoId) return;
        if(this.state.checking || this.state.outcome !== null) return;
        permissionState().then((state) => {
            if(!this.mounted) return;
            if(state === GRANTED){ this.check(); return; }
            // GEO-611 -- we are about to show the gate rather than resolve it
            // silently, so this player is being asked. Saying so is what makes
            // a later silence mean "asked and did not answer" instead of
            // "we have no idea", which is the entire question this is here to
            // settle. DENIED is reported as its own outcome: the browser is
            // holding a refusal from before we asked anything, read without
            // prompting, and that is a different person from one who is about
            // to see a prompt for the first time.
            this.report(state === "denied" ? "perm_blocked" : "prompt_shown");
        });
    }

    sessionId(){
        let session = readRemoteSession();
        return (session && session.sessionId) || "";
    }

    check(){
        if(this.state.checking || !this.props.promoId) return;

        // WEB-411 -- an empty session id cannot be answered, so ask the
        // browser for nothing. Catching it here means a player whose tab lost
        // its session is not made to hand over their location first and told
        // the visit is over afterwards.
        if(!this.sessionId() && this.endVisit(true)) return;

        this.setState({ checking: true, error: false });

        readPosition().then((position) => {
            if(!this.mounted) return;

            if(position.status !== GRANTED){
                // GEO-610 -- this return is the hole. Everything the browser
                // told us -- refused, refused by the device rather than the
                // person, no fix, no geolocation at all -- ended here and was
                // never sent anywhere, so all four arrived in the audit as the
                // same absence of a row. geo.js had already worked out which
                // one it was.
                this.report(position.status);
                this.setState({ checking: false, outcome: position.status });
                return;
            }

            // The reading worked. Anything that fails from here is ours, and
            // must not be reported as the browser refusing -- that is what
            // sent three rounds of debugging at the wrong thing when
            // verifyOnsite did not exist and every attempt was blamed on the
            // player's location settings.
            return GameService.verifyOnsite(
                this.sessionId(), this.props.promoId, position
            ).then((gate) => {
                if(!this.mounted) return;
                this.setState({
                    checking: false,
                    // Granted and inside is not an outcome we render — the
                    // parent unmounts us. Granted and outside is.
                    outcome: GRANTED,
                });
                if(gate && this.props.onVerified) this.props.onVerified(gate);
            }).catch((error) => {
                if(!this.mounted) return;
                if(this.handledExpiredSession(error)) return;
                this.setState({ checking: false, error: true, failure: "server" });
            });
        }).catch(() => {
            if(!this.mounted) return;
            this.setState({ checking: false, error: true, failure: "position" });
        });
    }

    /**
     * WEB-410 -- an expired visit is not a location failure.
     *
     * The server answers 410 when the launch session has aged out. That is the
     * end of the visit, not a problem with the player's location or with the
     * network -- but it arrived in the generic failure branch and was reported
     * as "we could not reach the game", which is untrue and, worse, offers a
     * Try again that can never succeed. A player could tap it forever.
     *
     * There is already a screen for this: "Thank you for playing", with a link
     * that mints a fresh session at /go/<slug>. One tap back into the game
     * instead of a dead end.
     *
     * The dead session id is cleared first, so the new visit cannot inherit it.
     */
    handledExpiredSession(error){
        var status = error && error.response && error.response.status;
        return this.endVisit(this.isOverStatus(status));
    }

    /**
     * WEB-411 -- 410 was not the only way a visit ends.
     *
     * The gate needs a session id AND a JWT, and they do not live together:
     * the id is in sessionStorage (dies with the tab, correctly) while the
     * token comes from AuthService in localStorage (survives it, for a day).
     * A browser restart therefore leaves a tab holding a token and no id, and
     * the server answers 401 for the stale token or 400 missing_sid for the
     * empty id.
     *
     * Neither is a location problem, and neither can be retried -- which is
     * precisely the trap 410 was routed away from. Sending a player round a
     * Try again that cannot work is the failure this component already exists
     * to have stopped doing, so all three ends go to the same place.
     *
     * 403 is deliberately NOT here: it means a real ownership failure, and
     * offering a fresh visit would paper over a bug worth seeing.
     */
    isOverStatus(status){
        return status === 410 || status === 401 || status === 400;
    }

    /**
     * WEB-412 -- at this gate, just start them over.
     *
     * SessionEnded says an automatic redirect is deliberately not done, and
     * that is right where it is written: a stale token bouncing to /go/ loops
     * in silence. But this gate is a special case, and Bill's is the better
     * reading of it -- we only ask for a location because the player has this
     * second walked up and opened the page. Somebody standing in the building
     * being told "thank you for playing" and asked to tap a link is a worse
     * answer than quietly giving them the visit they were trying to start.
     *
     * Nothing is bypassed by it. /go/ mints an unverified session, so they
     * still face exactly this check; the only thing skipped is the tap.
     *
     * The loop is the one real harm, so it is bounded rather than argued
     * away: one automatic restart, and a second inside the window falls
     * through to /session-ended, which is the human-tap screen that cannot
     * loop. A misconfiguration therefore costs a player one extra page load,
     * not an infinite spin.
     *
     * Only for a standalone web visit. readWebReturn is the slug /go/ keeps
     * in localStorage, so its presence is what says there is a visit to
     * restart at all. Without it -- a kiosk, a partner iframe -- the old
     * behaviour stands: an expired kiosk login is staff's problem, not a
     * player's, and must not be papered over with a new session.
     */
    endVisit(applies){
        if(!applies) return false;
        // Read the destination BEFORE clearing, and through restartPath, which
        // asks the session which venue it belonged to before falling back to
        // the single remembered slug. That key holds only the most recently
        // opened venue, so on a device that has seen two it could send a
        // player to the wrong casino.
        var path = restartPath();
        if(!path) return false;

        clearRemoteSession();
        window.location.assign(this.restartedRecently() ? "/session-ended" : path);
        return true;
    }

    /** True if we already restarted this browser inside the last minute. */
    restartedRecently(){
        var key = "pt-web-autorestart";
        try {
            var last = parseInt(localStorage.getItem(key), 10);
            var now = Date.now();
            // A minute is long enough that a genuine loop trips it on the
            // second pass, and short enough that a player who really does
            // return an hour later is restarted rather than sent to a screen
            // telling them something they did not do wrong.
            if(last && (now - last) < 60000) return true;
            localStorage.setItem(key, String(now));
        } catch (e) {
            // Storage unavailable (private mode, blocked). Prefer the screen
            // that cannot loop over a restart we would be unable to count.
            return true;
        }
        return false;
    }

    /**
     * What to say. Deliberately does NOT try to say who refused.
     *
     * It used to. A branch for the player denying the prompt, one for the
     * device, one for an insecure page -- and the browser does not give us
     * what we need to tell them apart. PERMISSION_DENIED comes back both when
     * a player says no and when Windows' own location switch is off, so the
     * screen confidently told somebody looking at a green "Location: allowed"
     * toggle that their browser was remembering a no they never said, and then
     * handed them instructions for undoing something that had not been done.
     * Wrong and a dead end at once.
     *
     * So it says the one thing true in every case -- we could not read your
     * location -- names both switches without blaming either, and ends with a
     * person. A patron on their phone will not debug this and must not be
     * asked to.
     */
    /**
     * GEO-592 -- what to say, split into a rule and the ways out of it.
     *
     * `lead` states the situation. `action` is the one thing they can do about
     * it from inside this browser, and gets its own line and a compass; the
     * guest wi-fi offer is the other way out and gets the line below it. The
     * three used to run together as one paragraph and the two choices were
     * invisible in the middle of it.
     *
     * Only the first-ask state has a discrete action to lift out. Every other
     * lead already contains its own instruction, and splitting those would
     * mean rewriting copy nobody asked to change.
     */
    copy(){
        let onsite = this.props.onsite || {};

        // A reading came back and matched no fence. The only case where we
        // know anything about where they are, and the server owns the words.
        if(this.state.outcome === GRANTED){
            return { lead: onsite.outside_message };
        }

        // Nothing attempted yet: say what is needed, claim nothing.
        if(this.state.outcome === null && !this.state.error){
            return {
                lead: onsite.visit_message || onsite.permission_message,
                // Older server, before location_message existed: fall back to
                // the whole sentence as the lead and show no action line,
                // rather than a compass beside nothing.
                action: onsite.visit_message ? onsite.location_message : null,
            };
        }

        if(this.state.failure === "server"){
            // Their browser did its part. Blaming their settings here is the
            // exact mistake this component has already made once.
            return { lead: "Your location came through, but we could not reach "
                 + "the game to check it. Please try again. If it keeps "
                 + "happening, please contact a player representative." };
        }

        return { lead: "We could not read your location. Please check that "
             + "location is switched on for this browser and for your device, "
             + "then try again. If you are on site, please contact a player "
             + "representative." };
    }

    /** GEO-590 -- shown in every state, or not at all when the venue has no
     *  network to join. */
    wifiMessage(){
        return (this.props.onsite || {}).wifi_message || "";
    }

    renderCopy(){
        let copy = this.copy();
        let wifi = this.wifiMessage();
        return (
            <div className="onsite-gate-copy">
                <p className="onsite-gate-message">{copy.lead}</p>
                {copy.action ? (
                    <p className="onsite-gate-line">
                        <FaCompass className="onsite-gate-icon"
                                   aria-hidden="true" />
                        <span>{copy.action}</span>
                    </p>
                ) : null}
                {wifi ? (
                    <p className="onsite-gate-line">
                        <FaWifi className="onsite-gate-icon"
                                aria-hidden="true" />
                        <span>{wifi}</span>
                    </p>
                ) : null}
            </div>
        );
    }

    /**
     * The heading, held to the same rule as the message: it must not claim to
     * know something that has not been checked.
     *
     * "You are not on site" over a button asking for location permission is a
     * conclusion drawn from a check that has not run, and is simply false for
     * a player standing in the building. Only a reading that came back and
     * matched no fence earns a heading about where they are.
     */
    title(){
        if(this.state.outcome === GRANTED) return "We could not place you on site";
        if(this.state.outcome === null && !this.state.error) return "Location needed";
        if(this.state.failure === "server") return "We could not reach the game";
        return "We could not check your location";
    }

    buttonLabel(){
        if(this.state.checking) return "Checking…";
        if(this.state.outcome === null && !this.state.error) return "Share my location";
        return "Try again";
    }

    /** Always. Nothing here is permanent, and a screen with no button is the
     *  dead end this whole component exists to avoid. */
    canRetry(){
        return true;
    }

    /**
     * GEO-591 -- what "Try again" should actually do.
     *
     * The refusal now tells a player they can connect to the guest wi-fi. The
     * network check, though, runs once, when the visit is created (GEO-504) --
     * so a player who reads that, joins the wi-fi and taps Try again gets
     * another location check and the identical answer. The one thing they just
     * did to fix it is the one thing that button never re-tests.
     *
     * So when the venue has a network to join, Try again mints a new visit
     * instead. /go/ runs the network check first and the location check after,
     * which is the order the player needs.
     *
     * The FIRST tap is excluded: that one is the permission prompt, and
     * reloading the page before anybody has been asked anything would be
     * absurd. Only a genuine retry restarts.
     *
     * Nothing is cleared on the way out. /go/ mints its own session, and if the
     * navigation never happens the player still has the one they arrived with.
     */
    retry(){
        let onsite = this.props.onsite || {};
        let firstAsk = this.state.outcome === null && !this.state.error;
        if(!firstAsk && onsite.wifi_retry){
            let path = restartPath();
            if(path){
                window.location.assign(path);
                return;
            }
        }
        this.check();
    }

    renderStatusNote(){
        // Shown in every state. Whatever stopped the location read, their
        // season and standings are still there -- and saying so is what keeps
        // this from being a screen with no way forward.
        let onsite = this.props.onsite || {};
        if(!onsite.status_message) return null;
        return <p className="onsite-gate-note">{onsite.status_message}</p>;
    }

    renderBody(){
        return (
            <div className="onsite-gate-body">
                <h2 className="onsite-gate-title">{this.title()}</h2>
                {this.renderCopy()}
                {this.renderStatusNote()}
                {this.canRetry() ? (
                    <Button
                        className="onsite-gate-btn"
                        disabled={this.state.checking}
                        onClick={() => this.retry()}>
                        {this.buttonLabel()}
                    </Button>
                ) : null}
                {this.props.variant === "overlay" ? (
                    <button type="button" className="onsite-gate-dismiss"
                            onClick={() => this.setState({dismissed: true})}>
                        Check my status and standings
                    </button>
                ) : null}
            </div>
        );
    }

    render(){
        let onsite = this.props.onsite;
        if(!onsite || !onsite.required || onsite.verified) return null;
        // GEO-410 -- the note says they may still check their standings, so
        // there has to be a way past this to do it. The grid underneath keeps
        // its own per-row refusal where PLAY would be (GEO-406), so nothing is
        // unlocked by stepping around the overlay.
        if(this.state.dismissed) return null;

        let overlay = this.props.variant === "overlay";
        return (
            <div className={overlay ? "onsite-gate onsite-gate-overlay" : "onsite-gate onsite-gate-inline"}>
                {this.renderBody()}
            </div>
        );
    }
}

export default OnsiteGate;
